import { useMemo, useState } from 'react';
import { getUserFromLocalStorage } from '../../utils/helper';
import { getTenant } from '../../utils/apis/tenants_repository';
import { getEntriesPerWeek, getOccupancyPerWeek, getTenantAlerts, getTenantReportsForRange } from '../../utils/apis/reports_repository';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import BarChart from '../../components/ui/charts/BarChart';
import LineChart from '../../components/ui/charts/LineChart';
import DoughnutChart from '../../components/ui/charts/DoughnutChart';
import { DoughnutLabel } from '../../components/ui/containers/DoughnutLabel';
import { ListItem } from '../../components/ui/containers/ListItem';
import { showToast } from '../../utils/toast';
import { generateTenantReportsPdf } from '../../utils/reportsPdf';

const ReportsBody = () => {

    const user = getUserFromLocalStorage();

    const currentDate = new Date();
    const [fromMonth, setFromMonth] = useState<number>(currentDate.getMonth() + 1);
    const [fromYear, setFromYear] = useState<number>(currentDate.getFullYear());
    const [toMonth, setToMonth] = useState<number>(currentDate.getMonth() + 1);
    const [toYear, setToYear] = useState<number>(currentDate.getFullYear());
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

    const monthOptions = useMemo(() => [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ], []);

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 6 }, (_, index) => currentYear - 2 + index);
    }, []);

    const invalidRange = useMemo(() => {
        const fromDate = new Date(fromYear, fromMonth - 1, 1);
        const toDate = new Date(toYear, toMonth - 1, 1);
        return fromDate > toDate;
    }, [fromMonth, fromYear, toMonth, toYear]);

    const handleGenerateTenantPdf = async () => {
        if (!user?.tenant) {
            showToast("Tenant information missing for the current user.", { type: "error" });
            return;
        }

        if (invalidRange) {
            showToast("From date must be earlier than To date.", { type: "error" });
            return;
        }

        setIsGeneratingPdf(true);
        try {
            const reports = await getTenantReportsForRange(
                user.tenant,
                { month: fromMonth, year: fromYear },
                { month: toMonth, year: toYear }
            );

            console.debug("[reports] PDF range results", reports);

            if (reports.length === 0) {
                showToast("No reporting data found for the selected range.", { type: "info" });
                return;
            }

            const rangeLabel = `${fromYear}-${`${fromMonth}`.padStart(2, "0")}_to_${toYear}-${`${toMonth}`.padStart(2, "0")}`;

            generateTenantReportsPdf(reports, user.tenant, rangeLabel, user.name || "Unknown User");
            showToast("Tenant statistics report downloaded.", { type: "success" });
        } catch (error) {
            console.error("Failed to generate tenant report", error);
            showToast("Something went wrong while generating the report.", { type: "error" });
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const { data: tenantData, isLoading: dataLoading, isError } = useQuery({
        queryKey: ['tenantData'],
        queryFn: () => getTenant(user.tenant),
    })

    const { data: occupancyData, isLoading: occupancyLoading, isError: occupancyError } = useQuery({
        queryKey: ['occupancyData'],
        queryFn: () => getOccupancyPerWeek(),
    })

    const { data: entryData, isLoading: entryLoading, isError: entryError } = useQuery({
        queryKey: ['entryData'],
        queryFn: () => getEntriesPerWeek(),
    })

    const { data: tenantAlerts, isLoading: tenantAlertsLoading, isError: tenantAlertsError } = useQuery({
        queryKey: ['tenantAlerts'],
        queryFn: () => getTenantAlerts(user.tenant),
    })

    if (isError || occupancyError || entryError || tenantAlertsError) {
        return <div className="flex justify-center">Something went wrong</div>
    }

    if (dataLoading || occupancyLoading || entryLoading || tenantAlertsLoading) {
        return <div className='flex justify-center items-center'>
            <Spinner />
        </div>
    }

    return (
        <>
            <div className='flex flex-col mx-14'>
                <div className="w-full bg-white rounded-md mb-6 p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                        <div>
                            <div className="text-xl font-bold text-gray-600">
                                Tenant Statistics Report
                            </div>
                            <div className="text-sm font-medium text-gray-400">
                                Select a month range to collate tenant booking statistics.
                            </div>
                        </div>
                        <button
                            disabled={isGeneratingPdf || invalidRange}
                            onClick={handleGenerateTenantPdf}
                            className="flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isGeneratingPdf ? (
                                <>
                                    <Spinner className="h-4 w-4 mr-2" fill="white" />
                                    Preparing PDF...
                                </>
                            ) : (
                                "Download Tenant Report"
                            )}
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div className="flex flex-col">
                            <label className="text-sm font-semibold text-gray-600 mb-1">From month</label>
                            <select
                                className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={fromMonth}
                                onChange={(event) => setFromMonth(Number(event.target.value))}
                            >
                                {monthOptions.map((month) => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-semibold text-gray-600 mb-1">From year</label>
                            <select
                                className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={fromYear}
                                onChange={(event) => setFromYear(Number(event.target.value))}
                            >
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-semibold text-gray-600 mb-1">To month</label>
                            <select
                                className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={toMonth}
                                onChange={(event) => setToMonth(Number(event.target.value))}
                            >
                                {monthOptions.map((month) => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-semibold text-gray-600 mb-1">To year</label>
                            <select
                                className="border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={toYear}
                                onChange={(event) => setToYear(Number(event.target.value))}
                            >
                                {yearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {invalidRange && (
                        <p className="text-xs text-red-600 mt-2">From date must be before To date.</p>
                    )}
                </div>
                <div className="w-full flex flex-row">
                    <div className="w-1/3 flex flex-row bg-white rounded-md">
                        <div className="flex flex-col items-center mx-10 my-2">
                            <div className="text-xl self-start font-bold text-gray-600 mt-2 mb-2">
                                Live Tenant Occupancy
                            </div>
                            <div className="text-sm font-medium text-gray-400 mb-2">
                                The number of Tenants at the car park along with the types of Tenants.
                            </div>
                            <div className='w-48 h-48'>

                                {
                                    dataLoading || tenantData === undefined ? <div className='justify-center items-center flex'>
                                        <Spinner className='justify-center items-center' />
                                    </div> :
                                        DoughnutChart({ chartData: tenantData })}
                            </div>
                            <DoughnutLabel />
                        </div>
                    </div>
                    <div className="w-2/3 flex flex-row ml-10 bg-white rounded-md">
                        <div className="flex flex-col w-full mx-10 my-3">
                            <div className="text-xl font-bold text-gray-600 mt-2">
                                Tenant Occupancy
                            </div>
                            <div className="text-sm font-medium text-gray-400 mb-2">
                                Breakdown of the number of Tenants at the car park.
                            </div>
                            <div className='w-full h-full my-3'>
                                {occupancyLoading || occupancyData === undefined ? <Spinner /> : <BarChart tenantName={user.tenant} chartData={occupancyData} />}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-full h-[450px] flex flex-row mt-6">
                    <div className="w-2/3 flex flex-row bg-white rounded-md">
                        <div className="flex flex-col w-full h-full mx-10">
                            <div className="text-xl font-bold text-gray-600 mt-4 mb-2">
                                Weekly Bookings
                            </div>
                            <div className='w-full h-full py-4'>
                                {entryLoading || entryData === undefined ? <Spinner /> :
                                    <LineChart chartData={entryData} tenantName={user.tenant} />}

                            </div>
                        </div>
                    </div>
                    <div className="w-1/3 ml-10 block flex-col bg-white rounded-md">
                        <div className="flex flex-col items-start justify-between mx-10 mt-5">
                            <div className="text-xl self-start font-bold text-gray-600">
                                Tenant Alerts
                            </div>
                            <div className="text-sm font-medium text-gray-400 mb-2">
                                Alerts received for the Tenant
                            </div>
                        </div>
                        {
                            tenantAlertsLoading || tenantAlerts === undefined ? <div className='justify-center items-center flex'>
                                <Spinner className='justify-center items-center block' />
                            </div> :
                                tenantAlerts.length > 0 ? tenantAlerts.slice(0, 3).map((alert, index) => {
                                    return <ListItem data={alert} key={index} />
                                }) : <div className='flex justify-center items-center'>No Alerts</div>
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

export default ReportsBody;
